import { forwardRef, useRef, useState, useImperativeHandle  } from 'react'
import { Box, Typography, TextField, FormLabel  } from "@mui/material";
import { styled } from '@mui/material/styles';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import BaseColorText from '../BaseColorText';
import BaseReactTooltip from '../BaseReactTooltip';
import { formatPhoneNumber } from "../../hooks/use-format-phone-number";

const CssTextField = styled(TextField)(({ theme, error, InputProps }) => ({
  '& label.Mui-focused': {
    color: '#5B87EF',
  },
  '& .MuiInput-underline:after': {
    borderBottomColor: '#5B87EF',
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: error ? '#ff0404' : InputProps.style?.border,
    },
    '&:hover fieldset': {
      borderColor: InputProps.style?.border,
    },
    '&.Mui-focused fieldset': {
      borderColor: error ? '#ff0404' : '#5B87EF',
    },
  },
}));

const PhoneNumberInput = forwardRef(({
  label,
  isHelpIcon,
  tooltipText,
  id,
  name,
  fullWidth = false,
  height = 47,
  message,
  error,
  sx,
  inputStyles = {
    color: '#46545F',
    fontWeight: 500,
    fontSize: '15px',
    border: '#ABB7C0',
    borderRadius: '8px',
    background: '#fff'
  },
  InputProps,
  required,
  ...props
    }, ref) => {
    const inputRef = useRef(null);
    const [phoneNumber, setPhoneNumber] = useState(props.value);
    useImperativeHandle(ref, () => inputRef.current);
    
    const handleInputChange = (e) => {
        const value = e.target.value;
        const cursorPosition = inputRef.current.selectionStart;

        // Apply formatting
        const formattedValue = formatPhoneNumber(value);
        setPhoneNumber(formattedValue);

        // Adjust the cursor position after formatting
        const newCursorPosition = calculateNewCursorPosition(cursorPosition, value, formattedValue);
        setTimeout(() => {
            inputRef.current.setSelectionRange(newCursorPosition, newCursorPosition);
        }, 0);
        props.onChange(formattedValue)
    };

    const handleBackspace = (e) => {
        const cursorPosition = inputRef.current.selectionStart;
        const currentValue = e.target.value;
        if (e.key === 'Backspace') {
            // If the cursor is at a formatting character, move it left to delete a digit
            if (cursorPosition > 0 && /[ )( \s-]/.test(currentValue[cursorPosition - 1])) {
                e.preventDefault(); // Prevent default backspace behavior
                const newCursorPosition = cursorPosition - 1;

                // Delete the digit to the left of the formatting character
                const updatedValue = currentValue.slice(0, newCursorPosition - 1) + currentValue.slice(newCursorPosition);

                // Format the updated value
                setPhoneNumber(formatPhoneNumber(updatedValue));

                // Set cursor position correctly after deletion
                setTimeout(() => {
                    inputRef.current.setSelectionRange(newCursorPosition - 1, newCursorPosition - 1);
                }, 0);
            }

            if(currentValue.length === 10 && !/[ )( \s-]/.test(currentValue[cursorPosition - 1]) ) {
                e.preventDefault();
                let newCursorPosition = cursorPosition;
                // Delete the digit to the left of the formatting character
                const updatedValue = currentValue.slice(0, newCursorPosition - 1) + currentValue.slice(newCursorPosition);
                // Format the updated value
                setPhoneNumber(formatPhoneNumber(updatedValue));

                if(currentValue[cursorPosition - 1] === '(') {
                    newCursorPosition = 5;
                }
                // Set cursor position correctly after deletion
                setTimeout(() => {
                    inputRef.current.setSelectionRange(newCursorPosition - 1, newCursorPosition - 1);
                }, 0);
            }

            if(currentValue.length > 6 && cursorPosition <=4 ) {
                e.preventDefault()
                setTimeout(() => {
                    inputRef.current.setSelectionRange(4, 4);
                }, 0);
                return false;
            }
            if(cursorPosition <= 3) {
                e.preventDefault()
                setTimeout(() => {
                    inputRef.current.setSelectionRange(4, 4);
                }, 0);
            }
        }
    };

    const calculateNewCursorPosition = (oldPosition, oldValue, newValue) => {
        let cursorDiff = 0;

        // Count non-digit characters before the old cursor position
        const oldNonDigitCount = (oldValue.slice(0, oldValue.length).match(/\D/g) || []).length;
        const newNonDigitCount = (newValue.slice(0, newValue.length).match(/\D/g) || []).length;

        // Calculate the difference in non-digit characters before the cursor
        cursorDiff = newNonDigitCount - oldNonDigitCount;
        if(!oldValue.includes('+1') && newValue.includes('+1')) {
            ++cursorDiff;
        }
        let newCursorPosition = oldPosition + cursorDiff;
        // while ((newValue.length <= oldValue.length) && newCursorPosition > 0 && /[ )( \s-]/.test(newValue[newCursorPosition - 1])) {
        //   --newCursorPosition;
        // }
        // Return the new cursor position, adjusting for non-digit characters
        return newCursorPosition;
    };
    
    const inputStyle = {
        fontWeight: 500,
        fontSize: '15px',
        border: error ? '#ff0404' : '#ABB7C0',
        borderRadius: '8px',
        background: '#fff',
        ...inputStyles,
        color: error ? '#ff0404' : inputStyles.color,
    }
    return (
        <Box 
            sx={{
                width: '100%',
                display: 'inline-flex',
                flexDirection: 'column',
                ...sx,

                '& .MuiOutlinedInput-notchedOutline': {
                border: `1px solid ${inputStyle.border}`,

                '&:hover': {
                    border: `1px solid ${inputStyle.border}`
                }
                },

                '& .MuiOutlinedInput-input': {
                height: height + 'px',
                paddingTop: 0,
                paddingBottom: 0,
                }
            }}
        >
            <Box
                sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
                }}
            >
                <Box>
                    {label &&
                        <FormLabel
                        sx={{
                            fontSize: '14px',
                            fontWeight: 500,
                            mb: '8px',
                            color: '#46545F'
                        }}
                        htmlFor={id}
                        >
                        {label}
                        {required && <BaseColorText color='#E73E3E'>*</BaseColorText>}
                        </FormLabel>
                    }
                </Box>
                    {isHelpIcon && (
                    <Box>
                        <HelpOutlineIcon 
                        sx={{
                            color: 'primary.mainBlue',
                            fontSize: '18px',
                            cursor: 'pointer'
                        }}
                        id={`${name}-input-help`}
                        />
                        <BaseReactTooltip
                        anchorSelect={`#${name}-input-help`}
                        place="bottom"
                        opacity={1}
                        style={{
                            zIndex: 20,
                            borderRadius: '8px',
                            boxShadow: '0px 2px 10px 0px #5B87EF33',
                        }}
                        >
                        <Box
                            sx={{
                            maxWidth: '327px',
                            }}
                        >
                            <Typography variant='subText' whiteSpace='pre-wrap' color='white'>
                            {tooltipText}
                            </Typography>
                        </Box>
                        </BaseReactTooltip>
                    </Box>
                )}
            </Box>
            <CssTextField
                inputRef={inputRef}
                {...props}
                autoComplete='off'
                fullWidth={fullWidth}
                InputProps={{
                style: {
                    ...inputStyle,
                },
                ...InputProps
                }}
                error={error}
                label={''}
                name={name}
                onChange={handleInputChange}
                onKeyDown={handleBackspace}
                value={phoneNumber}
            />
            {message && (
                <Typography variant='subText'>{message}</Typography>
            )}
        </Box>
    )
})

export default PhoneNumberInput
